import type { SubmissionAnswer, SubmissionQuestion } from '../types/formSubmissions';

// Fase 25A-R: presentacion PURA de una respuesta enviada como valor legible (nunca IDs/JSON/booleanos
// crudos). Separado del componente para poder probarlo. "Sin respuesta" solo se usa en preguntas
// VISIBLES sin valor (la visibilidad la decide el evaluador 23A/23B; las ocultas ni se renderizan).

export interface AnswerDisplay {
  isEmpty: boolean;
  // multiline: el texto largo debe conservar saltos de linea al mostrarse.
  multiline: boolean;
  text: string;
}

const EMPTY: AnswerDisplay = { isEmpty: true, multiline: false, text: 'Sin respuesta' };

// 'YYYY-MM-DD' -> fecha local sin corrimiento de zona horaria (se arma con partes, no con Date(iso)).
export function formatAnswerDate(d: string, locale = 'es-MX'): string {
  const [y, m, day] = d.split('-').map(Number);
  if (!y || !m || !day) return d;
  return new Date(y, m - 1, day).toLocaleDateString(locale, { dateStyle: 'medium' });
}

export function formatAnswerDisplay(question: SubmissionQuestion, answer: SubmissionAnswer | undefined, locale = 'es-MX'): AnswerDisplay {
  if (!answer) return EMPTY;
  switch (question.type) {
    case 'YES_NO':
      if (answer.valueBool === true) return { isEmpty: false, multiline: false, text: 'Sí' };
      if (answer.valueBool === false) return { isEmpty: false, multiline: false, text: 'No' };
      return EMPTY;
    case 'NUMBER':
      return answer.valueNumber === null || answer.valueNumber === undefined ? EMPTY : { isEmpty: false, multiline: false, text: String(answer.valueNumber) };
    case 'SHORT_TEXT':
      return answer.valueText && answer.valueText.trim() !== '' ? { isEmpty: false, multiline: false, text: answer.valueText } : EMPTY;
    case 'LONG_TEXT':
      return answer.valueText && answer.valueText.trim() !== '' ? { isEmpty: false, multiline: true, text: answer.valueText } : EMPTY;
    case 'DATE':
      return answer.valueDate ? { isEmpty: false, multiline: false, text: formatAnswerDate(answer.valueDate, locale) } : EMPTY;
    case 'TIME':
      return answer.valueTime ? { isEmpty: false, multiline: false, text: answer.valueTime } : EMPTY;
    case 'SINGLE_SELECT': {
      const opt = question.options.find((o) => answer.optionIds.includes(o.id));
      return opt ? { isEmpty: false, multiline: false, text: opt.label } : EMPTY;
    }
    case 'MULTI_SELECT': {
      const labels = question.options.filter((o) => answer.optionIds.includes(o.id)).map((o) => o.label);
      return labels.length ? { isEmpty: false, multiline: false, text: labels.join(', ') } : EMPTY;
    }
    default:
      return EMPTY;
  }
}
